import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import { SEARCH_IMGS_NOT_LAZY_LOADED } from 'constants/appConstants';
import { evSearchPageClickthrough } from 'events/search';
import { onEvent } from 'helpers/EventHelpers';
import { Loader } from 'components/Loader';
import SearchInlineRecos from 'components/search/SearchInlineRecos';
import SearchSlot from 'components/search/SearchSlot';
import { track } from 'apis/amethyst';
import { trackEvent } from 'helpers/analytics';
import SearchFeedback from 'components/search/SearchFeedback';
import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import SponsoredProducts from 'components/search/SponsoredProducts';
import CrossSiteProducts from 'components/search/CrossSiteProducts';
import { MartyContext } from 'utils/context';
import ProductCard from 'components/common/ProductCard';
import { SEARCH_PAGE } from 'constants/amethystPageTypes';
import { getHeartProps } from 'helpers/HeartUtils';
import { toUSD } from 'helpers/NumberFormats';

import css from 'styles/components/search/products.scss';

export class Products extends Component {
  static displayName = 'Products';

  state = {
    relatedStyleOpenedIndex: null,
    // Active animation index for products that get detail zoom
    animationTimerIndex: 0
  };

  componentDidMount() {
    onEvent(window, 'click', this.handleDocClick, null, this);
    onEvent(window, 'keydown', this.handleKeydownClick, null, this);
    const { products: { list } } = this.props;
    list.some(item => item.animationImages?.length) && this.startAnimationTimer(); // if we find a product that has animation images, start timer
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { products, layout, page, heartsData = {}, colorSwatchToggleState } = this.props;
    const { relatedStyleOpenedIndex, animationTimerIndex } = this.state;
    const { hearts } = heartsData;
    const { nextHeartsData = {} } = nextProps;
    const { nextHearts } = nextHeartsData;
    if (animationTimerIndex !== nextState.animationTimerIndex) {
      return true;
    }
    return nextProps.products !== products
      || nextProps.layout !== layout
      || nextProps.page !== page
      || nextHearts !== hearts
      || nextProps.colorSwatchToggleState !== colorSwatchToggleState
      || nextState.relatedStyleOpenedIndex !== relatedStyleOpenedIndex;
  }

  componentWillUnmount() {
    this.stopAnimationTimer();
  }

  // Set an initial interval ID that we can clear later when the component unmounts so we don't start two animation timers
  intervalID = 0;

  // Start a global animation timer for any products that have detail zoom.  Having a top level timer keeps them in sync regardless
  // of re-renders or lazyloading, which will tick every 800ms
  startAnimationTimer = () => {
    const MAX_ANIMATION_FRAMES = 9;
    this.intervalID = setInterval(() => {
      let newIndex = this.state.animationTimerIndex + 1;

      // Reset the cycle
      if (newIndex > MAX_ANIMATION_FRAMES) {
        newIndex = 0;
      }
      this.setState({
        animationTimerIndex: newIndex
      });
    }, 800);
  };

  stopAnimationTimer = () => {
    clearInterval(this.intervalID);
  };

  handleDocClick = e => {
    const container = e.target.closest('[data-card-related-styles-container]');
    if (!container) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  };

  handleKeydownClick = e => {
    if (e.keyCode === 27) {
      this.setState({ relatedStyleOpenedIndex: null });
    }
  };

  getLayoutStyles(layout) {
    const { hydraHdSearchImages } = this.props;
    switch (layout) {
      case 'two-item':
        return 'searchTwoWideMobile';
      case 'three-item':
        return 'searchThreeWideMobile';
      case 'four-item':
        return 'searchFourWideMobile';
      default:
        if (hydraHdSearchImages) {
          return 'searchTwoWideMobile';
        } else {
          return 'searchThreeWideMobile';
        }
    }
  }

  setRelatedStyleOpenedIndex = index => this.setState({ relatedStyleOpenedIndex: index });

  makeCardClick = (product, index) => {
    const { onProductClicked, filters: { page }, heartsData = {} } = this.props;
    const { heartsList = {} } = heartsData;
    const { productId, styleId, relatedStyles } = product;
    return e => {
      const position = index + 1;
      onProductClicked(position, productId, styleId, e);
      track(() => ([
        evSearchPageClickthrough, {
          pageNumber: page + 1,
          pageResultNumber: position,
          product,
          numberOfHearts: heartsList,
          swatchCount: relatedStyles?.length
        }
      ]));
    };
  };

  makeProducts() {
    const {
      productClass,
      ProductComponent,
      filters: { page, term, selected },
      products: { list, allProductsCount, inlineRecos, trustedRetailers },
      layout,
      onInlineRecoClick,
      heartsData = {},
      msaImageParams,
      showRatingStars,
      hydraSearchColorSwatches,
      hydraNewProductCard,
      colorSwatchToggleState,
      sponsoredProducts,
      inlineBannerData,
      trackSponsoredProductEvent,
      hydraPDPProductAngles,
      hydraCrossSiteSelling,
      hydraHdSearchImages,
      getProductInfo
    } = this.props;

    const {
      onHeartClick,
      hearts,
      heartsList = {}
    } = heartsData;
    const { testId } = this.context;
    const { animationTimerIndex } = this.state;

    if (allProductsCount > 0) {
      const layoutStyle = this.getLayoutStyles(layout);
      const additionalClasses = {
        container: layoutStyle
      };

      const sponsoredCards = (sponsoredProducts?.styles || []).map(v => ({
        ...v,
        productSeoUrl: v.link,
        price: toUSD(v.price),
        originalPrice: toUSD(v.originalPrice),
        isSponsored: true
      }));

      const combinedProducts = [...sponsoredCards, ...trustedRetailers, ...list];

      const { isCustomer, products, heartProduct, unHeartProduct, toggleHeartingLoginModal } = this.props;

      const heartsProps = getHeartProps({
        trackEvent,
        hearts,
        isCustomer,
        products,
        heartProduct,
        toggleHeartingLoginModal,
        unHeartProduct,
        hasHearting: true
      }, {
        addedFrom: SEARCH_PAGE,
        heartEventName: 'TE_SEARCH_PRODUCT_HEART',
        unHeartEventName: 'TE_SEARCH_PRODUCT_UNHEART'
      });

      const heartsInfo = {
        heartsData: heartsProps,
        isCustomer,
        products,
        heartProduct,
        unHeartProduct,
        toggleHeartingLoginModal
      };

      return (
        <div className={cn(css.products, productClass)}>
          {!hydraNewProductCard && hydraCrossSiteSelling && <CrossSiteProducts
            additionalClasses={additionalClasses}
            trustedRetailers={trustedRetailers}
            msaImageParams={msaImageParams}
            showRatingStars={showRatingStars}
            hydraSearchColorSwatches={hydraSearchColorSwatches}
            hydraHdSearchImages={hydraHdSearchImages}
            ProductComponent={ProductComponent}
            getProductInfo={getProductInfo}/>}
          {hydraNewProductCard && combinedProducts.map((v, i) => {
            const product = { ...v, index: i };
            product.msaImageParams = msaImageParams;
            product.hearts = heartsInfo;
            product.onClick = this.makeCardClick(v, i);
            return <ProductCard key={`${product.styleId}-${product.colorId}`} {...product} />;
          })}
          {!hydraNewProductCard && list.map((v, i) => (
            <ProductComponent
              key={`${v.styleId}-${v.colorId}`}
              componentStyle="searchProduct"
              onComponentClick={this.makeCardClick(v, i)}
              heartsData={heartsData}
              additionalClasses={additionalClasses}
              animationTimerIndex={animationTimerIndex}
              cardData={v}
              index={i}
              msaImageParams={msaImageParams}
              melodyCardTestId={testId('searchResult')}
              showRatingStars={showRatingStars}
              shouldLazyLoad={i > SEARCH_IMGS_NOT_LAZY_LOADED}
              colorSwatchToggleState={colorSwatchToggleState}
              hydraPDPProductAngles={hydraPDPProductAngles}
              hydraSearchColorSwatches={hydraSearchColorSwatches}
              hydraHdSearchImages={hydraHdSearchImages}
              swatchClickCallback={(swatch, swatchIndex) => {
                const index = i + 1;
                track(() => ([
                  evSearchPageClickthrough, {
                    pageNumber: page + 1,
                    pageResultNumber: index,
                    product: swatch,
                    numberOfHearts: heartsList[v.styleId],
                    swatchCount: v.relatedStyles?.length,
                    swatchIndex
                  }
                ]));
              }}
              isRelatedStyleDropdownOpen={this.state.relatedStyleOpenedIndex === i}
              setRelatedStyleOpenedIndex={this.setRelatedStyleOpenedIndex}/>
          ))}
          {!hydraNewProductCard && sponsoredProducts?.styles
            ?
            <SponsoredProducts
              sponsoredProducts={sponsoredProducts}
              onHeartClick={onHeartClick}
              msaImageParams={msaImageParams}
              hearts={hearts}
              showRatingStars={showRatingStars}
              heartsList={heartsList}
              heartsData={heartsData}
              searchTerm={term}
              layoutStyle={layoutStyle}
              trackSponsoredProductEvent={trackSponsoredProductEvent}
              hydraHdSearchImages={hydraHdSearchImages}
            />
            :
            !hydraNewProductCard && !!inlineRecos?.recos?.length &&
            <SearchInlineRecos
              heartsData={heartsData}
              showHearting={heartsData.showFavoriteHeart}
              onHeartClick={onHeartClick}
              inlineRecos={inlineRecos}
              onInlineRecoClick={onInlineRecoClick}
              msaImageParams={msaImageParams}
              hearts={hearts}
              showRatingStars={showRatingStars}
              heartsList={heartsList}
              searchTerm={term}
              hydraHdSearchImages={hydraHdSearchImages}
            />}
          {/* We only display the inline ad if there are enough products to have a 4th row at max products per row (5 x 3) = 15 */}
          {!hydraNewProductCard && list.length > 15 && inlineBannerData && (
            <SearchSlot
              slotDetails={inlineBannerData}
              layoutStyle={layoutStyle}
              searchTerm={term}
              selectedFilters={selected}
              hydraHdSearchImages={hydraHdSearchImages}
            />
          )}
        </div>
      );
    }
    return null;// no results view handled by NoSearchResults.js
  }

  makeSearchFeedback = () => {
    const { location, products: { isLoading }, handleSearchFeedbackClick, feedbackSubmitted } = this.props;
    const { marketplace: { search: { showSearchFeedback } } } = this.context;
    if (location?.query?.oosRedirected) { // check if this is a pdp oos redirect
      return null;
    }
    return !isLoading && showSearchFeedback && <SearchFeedback handleClick={handleSearchFeedbackClick} feedbackSubmitted={feedbackSubmitted} />;
  };

  render() {
    const { makeScrollButton, showLoader, products: { oosMessaging, isLoading }, pixelServerHost, hydraNewProductCard } = this.props;

    return (
      <MartyContext.Consumer>
        {({
          testId,
          marketplace: {
            search: {
              hasMicrosoftAds
            }
          }
        }) => (
          <div id="searchPage" className="searchPage">
            {!hydraNewProductCard && this.makeSearchFeedback()}
            <h2>Search Results</h2>
            {oosMessaging && <p className={css.oosMessaging} data-test-id={testId('oosMessage')}>{oosMessaging}</p>}
            {hasMicrosoftAds && pixelServerHost && <iframe
              className={css.misad}
              frameBorder="0"
              title="misad"
              src={`https://${pixelServerHost}/ads/microsoft-ads.html`} />}
            {showLoader && isLoading ? <Loader /> : this.makeProducts()}
            {makeScrollButton && makeScrollButton()}
          </div>
        )}
      </MartyContext.Consumer>
    );
  }
}

Products.propTypes = {
  filters: PropTypes.object.isRequired,
  layout: PropTypes.string,
  onProductClicked: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number,
  products: PropTypes.object.isRequired,
  showRatings: PropTypes.bool.isRequired,
  onInlineRecoClick: PropTypes.func,
  msaImageParams: PropTypes.object.isRequired
};

Products.contextTypes = {
  testId: PropTypes.func,
  marketplace: PropTypes.object
};

export default withErrorBoundary('Products', Products);
